// 활동지 v2 모델 — 단일 진실 공급원은 `.claude/skills/task-editor/SKILL.md`.
// JSON 키는 snake_case. id 필드 없음 — 위치는 배열 index 로 식별.
// Block shape: { description: DescriptionComponent[], action: ActionComponent | null }

// ─── 상수 ─────────────────────────────────────────────────────────────────

export const TASK_EDITOR_DESCRIPTION_COMPONENT_TYPES = [
  "text",
  "image",
  "reference_automatic_thought",
  "reference_coach_preset",
] as const;

export const TASK_EDITOR_ACTION_COMPONENT_TYPES = [
  "text_input",
  "button_default",
  "button_quiz",
  "button_branch",
  "audio",
] as const;

export const TASK_EDITOR_COMPONENT_TYPES = [
  ...TASK_EDITOR_DESCRIPTION_COMPONENT_TYPES,
  ...TASK_EDITOR_ACTION_COMPONENT_TYPES,
] as const;

export const TASK_EDITOR_TYPOGRAPHY_STYLES = [
  "header",
  "body",
  "label",
] as const;

// block 단위 type — 미리보기 렌더 시 header/body 스타일 분기에 사용한다.
// description[0].text 의 typography_style 과 함께 가게끔 동기화하지만,
// "블록이 헤더인지 본문인지" 라는 의도는 block.type 에 직접 표현한다.
export const TASK_EDITOR_BLOCK_TYPES = ["header", "body"] as const;
export type TaskEditorBlockType = (typeof TASK_EDITOR_BLOCK_TYPES)[number];

// UI 카테고리 (텍스트 / 이미지 / 불러오기 / 텍스트 입력 / 버튼).
export const TASK_EDITOR_COMPONENT_CATEGORIES = [
  "text",
  "image",
  "reference",
  "text_input",
  "button",
] as const;
export type TaskEditorComponentCategory =
  (typeof TASK_EDITOR_COMPONENT_CATEGORIES)[number];

// ─── 타입 ────────────────────────────────────────────────────────────────

export type TaskEditorDescriptionComponentType =
  (typeof TASK_EDITOR_DESCRIPTION_COMPONENT_TYPES)[number];
export type TaskEditorActionComponentType =
  (typeof TASK_EDITOR_ACTION_COMPONENT_TYPES)[number];
export type TaskEditorComponentType =
  | TaskEditorDescriptionComponentType
  | TaskEditorActionComponentType;
export type TaskEditorComponentSection = "description" | "action";
export type TaskEditorTypographyStyle =
  (typeof TASK_EDITOR_TYPOGRAPHY_STYLES)[number];

export interface TaskEditorButtonOption {
  text: string;
  is_correct?: boolean; // button_quiz
  show_from?: number; // button_branch — 1-based block sequence
  show_to?: number;
  restart_from?: number;
  hide_from?: number;
}

export interface TaskEditorTextInputField {
  label?: string;
  placeholder?: string;
  help_text?: string;
  // 기본 필수. true 일 때만 저장 (is_correct 와 동일 컨벤션) — 미리보기 진행
  // 게이팅에서 제외 (v1 의 optional 필드와 동일).
  optional?: boolean;
}

export type TaskEditorDescriptionComponent =
  | {
      type: "text";
      content: string;
      typography_style: TaskEditorTypographyStyle;
    }
  | { type: "image"; content: string }
  | { type: "reference_automatic_thought" }
  | { type: "reference_coach_preset"; preset_text: string };

export type TaskEditorActionComponent =
  | { type: "text_input"; fields: TaskEditorTextInputField[] }
  | {
      type: "button_default";
      options: TaskEditorButtonOption[];
      allow_multiple_selection: boolean;
    }
  | {
      type: "button_quiz";
      options: TaskEditorButtonOption[];
    }
  | { type: "button_branch"; options: TaskEditorButtonOption[] }
  | {
      // 명상 등에서 쓰는 오디오 플레이어. 미리보기/체험은 v1 명상 player 와 동일.
      // artwork 는 이미지 컴포넌트와 동일하게 URL 또는 업로드 data URL.
      // audio_url / extra_audio_url 은 호스팅된 오디오 파일의 전체 URL.
      type: "audio";
      artwork: string;
      audio_url: string;
      // 선택 — 본 오디오가 끝난 뒤 반복 재생할 추가 오디오. 빈 값이면 키 생략.
      extra_audio_url?: string;
    };

export type TaskEditorComponent =
  | TaskEditorDescriptionComponent
  | TaskEditorActionComponent;

// 블록은 description 배열 + 선택적 action 객체. id 없음.
// type 은 미리보기에서 header/body 스타일 분기에 쓰이며, 첫 텍스트의
// typography_style 과 동기화된다.
export interface TaskEditorBlock {
  type: TaskEditorBlockType;
  description: TaskEditorDescriptionComponent[];
  action: TaskEditorActionComponent | null;
}

export interface TaskEditorState {
  task_id: string;
  title: string;
  // 필수값. 미분류 = 9999 (UNCATEGORIZED_SET_NUMBER). undefined 없음.
  set_number: number;
  is_key_activity?: boolean;
  // 같은 set_number 안에서의 정렬 키 (오름차순). 백엔드는 NOT NULL 이지만
  // 로컬 신규 활동지는 reorder/commit 전까지 미설정일 수 있어 optional.
  sort_order?: number;
  blocks: TaskEditorBlock[];
}

// 미분류 sentinel. 와이어(서버) 규약상 set_number 는 NOT NULL 이고 미분류 = 9999.
// 프론트 내부 모델은 "undefined = 미분류"를 쓰므로 경계에서만 9999 ↔ undefined 매핑한다.
export const UNCATEGORIZED_SET_NUMBER = 9999;

// task-meta/save(단건)·save-all(배치) 의 메타 페이로드 — 레코드당 전체 메타(full replace).
// 프론트 내부 모델과 동일한 snake_case. set_number 는 NOT NULL (미분류 = 9999), sort_order 는 정수 NOT NULL.
// (모델 레이어에 정의 — api 와 listUtils 가 함께 사용.)
export interface TaskMetaUpdate {
  task_id: string;
  title: string;
  set_number: number;
  is_key_activity: boolean;
  sort_order: number;
}

// 편집 UI 의 위치 식별자 — 섹션 단위로 구분.
export type TaskEditorComponentLocation =
  | { blockIndex: number; section: "description"; descriptionIndex: number }
  | { blockIndex: number; section: "action" };

export type TaskEditorDeleteTarget =
  | { type: "block"; blockIndex: number }
  | { type: "component"; location: TaskEditorComponentLocation };

export type TaskEditorBlockDropPreview = { insertionIndex: number };

// 컴포넌트 드래그는 description 배열 내부에서만 동작 (action 은 standalone).
export type TaskEditorComponentDragData = {
  blockIndex: number;
  descriptionIndex: number;
};

export type TaskEditorComponentDropPreview = {
  blockIndex: number;
  insertionIndex: number;
};

// ─── 분류 판별 ────────────────────────────────────────────────────────────

export function isTaskEditorDescriptionComponentType(
  type: TaskEditorComponentType
): type is TaskEditorDescriptionComponentType {
  return (
    [...TASK_EDITOR_DESCRIPTION_COMPONENT_TYPES] as readonly string[]
  ).includes(type);
}

export function isTaskEditorActionComponentType(
  type: TaskEditorComponentType
): type is TaskEditorActionComponentType {
  return (
    [...TASK_EDITOR_ACTION_COMPONENT_TYPES] as readonly string[]
  ).includes(type);
}

export function getTaskEditorComponentSection(
  type: TaskEditorComponentType
): TaskEditorComponentSection {
  return isTaskEditorDescriptionComponentType(type) ? "description" : "action";
}

export function getTaskEditorComponentSectionLabel(
  section: TaskEditorComponentSection
): string {
  return section === "description" ? "설명" : "유저 액션";
}

export function getTaskEditorComponentCategory(
  type: TaskEditorComponentType
): TaskEditorComponentCategory {
  if (type === "text") return "text";
  if (type === "image") return "image";
  if (
    type === "reference_automatic_thought" ||
    type === "reference_coach_preset"
  ) {
    return "reference";
  }
  if (type === "text_input") return "text_input";
  return "button";
}

// ─── 라벨 ─────────────────────────────────────────────────────────────────

export function getTaskEditorCategoryLabel(
  category: TaskEditorComponentCategory
) {
  switch (category) {
    case "text":
      return "텍스트";
    case "image":
      return "이미지";
    case "reference":
      return "불러오기";
    case "text_input":
      return "텍스트 입력";
    case "button":
      return "버튼";
    default:
      return category;
  }
}

export function getTaskEditorComponentTypeLabel(type: TaskEditorComponentType) {
  switch (type) {
    case "text":
      return "텍스트";
    case "image":
      return "이미지";
    case "reference_automatic_thought":
      return "자동적 사고";
    case "reference_coach_preset":
      return "코치 프리셋";
    case "text_input":
      return "주관식";
    case "button_default":
      return "객관식";
    case "button_quiz":
      return "객관식 (정답형)";
    case "button_branch":
      return "객관식 (분기형)";
    case "audio":
      return "오디오";
    default:
      return type;
  }
}

// button_quiz 옵션의 고정 글자 라벨(A, B, C…). index 기반 표현이며 text 에 저장하지 않는다.
// 26개 초과(범위 밖)는 표시 라벨이 없으므로 빈 문자열 — 운영상 도달하지 않는 경계.
export function getTaskEditorQuizOptionLabel(index: number): string {
  if (index < 0 || index >= 26) return "";
  return String.fromCharCode(65 + index);
}

export function getTaskEditorTypographyStyleLabel(
  style: TaskEditorTypographyStyle
) {
  switch (style) {
    case "header":
      return "header";
    case "label":
      return "label";
    default:
      return "body";
  }
}

// ─── 생성자 ───────────────────────────────────────────────────────────────

export function createTaskEditorButtonOption(
  value?: Partial<TaskEditorButtonOption>
): TaskEditorButtonOption {
  return {
    text: typeof value?.text === "string" ? value.text : "",
    ...(typeof value?.show_from === "number"
      ? { show_from: value.show_from }
      : {}),
    ...(typeof value?.show_to === "number" ? { show_to: value.show_to } : {}),
    ...(typeof value?.restart_from === "number"
      ? { restart_from: value.restart_from }
      : {}),
    ...(typeof value?.hide_from === "number"
      ? { hide_from: value.hide_from }
      : {}),
    ...(value?.is_correct === true ? { is_correct: true } : {}),
  };
}

export function createTaskEditorTextInputField(): TaskEditorTextInputField {
  return {};
}

export function createTaskEditorDescriptionComponent(
  type: TaskEditorDescriptionComponentType = "text"
): TaskEditorDescriptionComponent {
  switch (type) {
    case "text":
      return { type: "text", content: "", typography_style: "body" };
    case "image":
      return { type: "image", content: "" };
    case "reference_automatic_thought":
      return { type: "reference_automatic_thought" };
    case "reference_coach_preset":
      return { type: "reference_coach_preset", preset_text: "" };
  }
}

export function createTaskEditorActionComponent(
  type: TaskEditorActionComponentType = "text_input"
): TaskEditorActionComponent {
  switch (type) {
    case "text_input":
      return { type: "text_input", fields: [createTaskEditorTextInputField()] };
    case "button_default":
      return {
        type: "button_default",
        allow_multiple_selection: true,
        options: [
          createTaskEditorButtonOption(),
          createTaskEditorButtonOption(),
        ],
      };
    case "button_quiz":
      return {
        type: "button_quiz",
        options: [
          createTaskEditorButtonOption(),
          createTaskEditorButtonOption(),
        ],
      };
    case "button_branch":
      return {
        type: "button_branch",
        options: [
          createTaskEditorButtonOption(),
          createTaskEditorButtonOption(),
        ],
      };
    case "audio":
      return { type: "audio", artwork: "", audio_url: "" };
  }
}

export function createTaskEditorComponent(
  type: TaskEditorComponentType = "text"
): TaskEditorComponent {
  if (isTaskEditorDescriptionComponentType(type)) {
    return createTaskEditorDescriptionComponent(type);
  }
  return createTaskEditorActionComponent(type);
}

export function createTaskEditorBlock(
  type: TaskEditorBlockType = "body"
): TaskEditorBlock {
  // header 블록은 description[0] 의 typography_style 을 header 로 맞춰 block.type 과
  // 동기화한다 (invariant #4). body 는 기존대로 body 스타일 text.
  const firstText = createTaskEditorDescriptionComponent("text");
  return {
    type,
    description: [
      type === "header" && firstText.type === "text"
        ? { ...firstText, typography_style: "header" }
        : firstText,
    ],
    action: null,
  };
}

// description[0].text 의 typography_style 에 맞춰 block.type 결정.
// header 텍스트로 시작하는 블록은 header, 그 외는 body.
export function deriveTaskEditorBlockType(
  block: Pick<TaskEditorBlock, "description">
): TaskEditorBlockType {
  const first = block.description[0];
  if (first && first.type === "text" && first.typography_style === "header") {
    return "header";
  }
  return "body";
}

// 카테고리 → 기본 데이터 type (UI add menu)
export function getTaskEditorCategoryDefaultType(
  category: TaskEditorComponentCategory
): TaskEditorComponentType {
  switch (category) {
    case "text":
      return "text";
    case "image":
      return "image";
    case "reference":
      return "reference_automatic_thought";
    case "text_input":
      return "text_input";
    case "button":
      return "button_default";
  }
}

// ─── reorder helpers ─────────────────────────────────────────────────────

function reorderArray<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  if (
    fromIndex < 0 ||
    fromIndex >= arr.length ||
    toIndex < 0 ||
    toIndex >= arr.length ||
    fromIndex === toIndex
  ) {
    return arr;
  }
  const next = [...arr];
  const [target] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, target);
  return next;
}

export const reorderBlocks = reorderArray;
export const reorderDescriptionComponents = reorderArray;

export function moveBlock(
  blocks: TaskEditorBlock[],
  index: number,
  direction: "up" | "down"
) {
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  return reorderArray(blocks, index, targetIndex);
}

// ─── 이미지 유틸 ──────────────────────────────────────────────────────────

export function convertTaskEditorImageFileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const result = fileReader.result;
      if (typeof result !== "string") {
        reject(new Error("이미지 파일을 읽지 못했습니다."));
        return;
      }
      resolve(result);
    };
    fileReader.onerror = () =>
      reject(new Error("이미지 파일을 읽지 못했습니다."));
    fileReader.readAsDataURL(file);
  });
}

export function isTaskEditorLocalImageDataUrl(value: string) {
  return value.trim().startsWith("data:image/");
}

// ─── 옵션 헬퍼 ───────────────────────────────────────────────────────────

export function normalizeTaskEditorButtonOption(
  option: unknown
): TaskEditorButtonOption {
  if (typeof option === "string") {
    return createTaskEditorButtonOption({ text: option });
  }
  if (!option || typeof option !== "object") {
    return createTaskEditorButtonOption();
  }
  const c = option as Record<string, unknown>;
  return createTaskEditorButtonOption({
    text: typeof c.text === "string" ? c.text : "",
    ...(typeof c.show_from === "number" ? { show_from: c.show_from } : {}),
    ...(typeof c.show_to === "number" ? { show_to: c.show_to } : {}),
    ...(typeof c.restart_from === "number"
      ? { restart_from: c.restart_from }
      : {}),
    ...(typeof c.hide_from === "number" ? { hide_from: c.hide_from } : {}),
    ...(c.is_correct === true ? { is_correct: true } : {}),
  });
}

export function getTaskEditorButtonOptions(
  options: TaskEditorButtonOption[] | undefined
) {
  if (!Array.isArray(options) || options.length === 0) {
    return [createTaskEditorButtonOption(), createTaskEditorButtonOption()];
  }
  return options.map((o) => normalizeTaskEditorButtonOption(o));
}

// ─── 분기 대상 sequence (button_branch) ──────────────────────────────────

// source 블록 (index 기준) 뒤에 위치한 블록들의 1-based sequence 만 반환.
// UI 는 sequence 만 표시하므로 blocks 배열 ref 의존을 끊어 메모이제이션 가능.
export function getTaskEditorBranchTargetSequences(
  blockCount: number,
  sourceBlockIndex: number
): number[] {
  const out: number[] = [];
  for (let i = sourceBlockIndex + 1; i < blockCount; i += 1) {
    out.push(i + 1);
  }
  return out;
}

// ─── 드롭존 인덱스 ────────────────────────────────────────────────────────

export function getTaskEditorBlockReorderIndex(
  blocks: TaskEditorBlock[],
  sourceBlockIndex: number,
  preview: TaskEditorBlockDropPreview
) {
  if (sourceBlockIndex < 0 || sourceBlockIndex >= blocks.length) {
    return null;
  }
  const nextIndex =
    preview.insertionIndex > sourceBlockIndex
      ? preview.insertionIndex - 1
      : preview.insertionIndex;
  return Math.max(0, Math.min(blocks.length - 1, nextIndex));
}

export function isSameTaskEditorBlockDropPreview(
  currentPreview: TaskEditorBlockDropPreview | null,
  nextPreview: TaskEditorBlockDropPreview
) {
  return currentPreview?.insertionIndex === nextPreview.insertionIndex;
}

export function getTaskEditorComponentReorderIndex(
  descriptionComponents: TaskEditorDescriptionComponent[],
  sourceDescriptionIndex: number,
  preview: TaskEditorComponentDropPreview
) {
  if (
    sourceDescriptionIndex < 0 ||
    sourceDescriptionIndex >= descriptionComponents.length
  ) {
    return null;
  }
  const nextIndex =
    preview.insertionIndex > sourceDescriptionIndex
      ? preview.insertionIndex - 1
      : preview.insertionIndex;
  return Math.max(0, Math.min(descriptionComponents.length - 1, nextIndex));
}

export function isSameTaskEditorComponentDropPreview(
  currentPreview: TaskEditorComponentDropPreview | null,
  nextPreview: TaskEditorComponentDropPreview
) {
  return (
    currentPreview?.blockIndex === nextPreview.blockIndex &&
    currentPreview?.insertionIndex === nextPreview.insertionIndex
  );
}

export function parseTaskEditorComponentDragData(rawData: string) {
  if (!rawData) return null;
  try {
    const parsed = JSON.parse(rawData) as Partial<TaskEditorComponentDragData>;
    if (
      typeof parsed.blockIndex !== "number" ||
      typeof parsed.descriptionIndex !== "number"
    ) {
      return null;
    }
    return {
      blockIndex: parsed.blockIndex,
      descriptionIndex: parsed.descriptionIndex,
    };
  } catch {
    return null;
  }
}

// ─── 단축키 ───────────────────────────────────────────────────────────────

export const isTaskEditorUndoShortcut = (event: KeyboardEvent) => {
  if (event.altKey || event.shiftKey || event.isComposing) {
    return false;
  }
  return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z";
};

// ─── Legacy migration ────────────────────────────────────────────────────
// 구 모델 데이터를 새 contract 로 변환. cloneTaskEditorState 가 진입점에서 호출.

function migrateTypographyStyle(value: unknown): TaskEditorTypographyStyle {
  if (typeof value !== "string") return "body";
  const v = value.toLowerCase();
  if (v === "header") return "header";
  if (v === "label") return "label";
  return "body";
}

function migrateButtonOption(option: unknown): TaskEditorButtonOption {
  if (typeof option === "string") {
    return createTaskEditorButtonOption({ text: option });
  }
  if (!option || typeof option !== "object") {
    return createTaskEditorButtonOption();
  }
  const c = option as Record<string, unknown>;
  return createTaskEditorButtonOption({
    text:
      typeof c.text === "string"
        ? c.text
        : typeof c.translationKey === "string"
          ? (c.translationKey as string)
          : "",
    ...(typeof c.show_from === "number" ? { show_from: c.show_from } : {}),
    ...(typeof c.show_to === "number" ? { show_to: c.show_to } : {}),
    ...(typeof c.restart_from === "number"
      ? { restart_from: c.restart_from }
      : {}),
    ...(typeof c.hide_from === "number" ? { hide_from: c.hide_from } : {}),
    ...(c.is_correct === true || c.isCorrect === true
      ? { is_correct: true }
      : {}),
  });
}

function pickOptionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  return value.length > 0 ? value : undefined;
}

function migrateTextInputField(value: unknown): TaskEditorTextInputField {
  if (!value || typeof value !== "object") {
    return createTaskEditorTextInputField();
  }
  const f = value as Record<string, unknown>;
  const labelEnabledLegacy = f.label_enabled ?? f.labelEnabled;
  const placeholderEnabledLegacy =
    f.placeholder_enabled ?? f.placeholderEnabled;
  const helpTextEnabledLegacy = f.help_text_enabled ?? f.helpTextEnabled;
  const labelValue = typeof f.label === "string" ? f.label : undefined;
  const placeholderValue =
    typeof f.placeholder === "string" ? f.placeholder : undefined;
  const helpTextValue =
    typeof f.help_text === "string"
      ? (f.help_text as string)
      : typeof f.helpText === "string"
        ? (f.helpText as string)
        : undefined;

  const result: TaskEditorTextInputField = {};
  if (labelEnabledLegacy !== false) {
    const v = pickOptionalString(labelValue);
    if (v !== undefined) result.label = v;
  }
  if (placeholderEnabledLegacy !== false) {
    const v = pickOptionalString(placeholderValue);
    if (v !== undefined) result.placeholder = v;
  }
  if (helpTextEnabledLegacy !== false) {
    const v = pickOptionalString(helpTextValue);
    if (v !== undefined) result.help_text = v;
  }
  // 기본 필수 — optional: true 일 때만 보존 (v1·신규 모두 동일 키).
  if (f.optional === true) result.optional = true;
  return result;
}

// 구·신 모든 raw component 를 description/action 중 하나로 변환.
// 반환된 객체로 섹션 판별 후 적절한 배열/슬롯에 배치.
function migrateLegacyComponent(value: unknown): TaskEditorComponent | null {
  if (!value || typeof value !== "object") return null;
  const c = value as Record<string, unknown>;
  const rawType = typeof c.type === "string" ? c.type : "";

  // 새 contract type 그대로
  switch (rawType) {
    case "text":
      return {
        type: "text",
        content: typeof c.content === "string" ? c.content : "",
        typography_style: migrateTypographyStyle(
          c.typography_style ?? c.typographyStyle
        ),
      };
    case "image":
      return {
        type: "image",
        content: typeof c.content === "string" ? c.content : "",
      };
    case "reference_automatic_thought":
      return { type: "reference_automatic_thought" };
    case "reference_coach_preset":
      return {
        type: "reference_coach_preset",
        preset_text:
          typeof c.preset_text === "string" ? (c.preset_text as string) : "",
      };
    case "text_input": {
      const fields = Array.isArray(c.fields)
        ? c.fields.map((f) => migrateTextInputField(f))
        : [createTaskEditorTextInputField()];
      return { type: "text_input", fields };
    }
    case "button_default":
      return {
        type: "button_default",
        allow_multiple_selection:
          (c.allow_multiple_selection ?? c.allowMultipleSelection) !== false,
        options: Array.isArray(c.options)
          ? c.options.map((o) => migrateButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "button_quiz":
      return {
        type: "button_quiz",
        options: Array.isArray(c.options)
          ? c.options.map((o) => migrateButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "button_branch":
      return {
        type: "button_branch",
        options: Array.isArray(c.options)
          ? c.options.map((o) => migrateButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "audio": {
      const extra =
        typeof c.extra_audio_url === "string" ? c.extra_audio_url : "";
      return {
        type: "audio",
        artwork: typeof c.artwork === "string" ? c.artwork : "",
        audio_url: typeof c.audio_url === "string" ? c.audio_url : "",
        ...(extra ? { extra_audio_url: extra } : {}),
      };
    }
  }

  // 구 contract → 새 contract 매핑
  switch (rawType) {
    case "Typography":
    case "TypographyLine":
      return {
        type: "text",
        content: typeof c.content === "string" ? c.content : "",
        typography_style: migrateTypographyStyle(c.typographyStyle),
      };
    case "Image":
      return {
        type: "image",
        content: typeof c.content === "string" ? c.content : "",
      };
    case "AutomaticThought":
      return { type: "reference_automatic_thought" };
    case "Preset":
      return {
        type: "reference_coach_preset",
        preset_text:
          typeof c.textareaPlaceholder === "string"
            ? (c.textareaPlaceholder as string)
            : typeof c.preset_text === "string"
              ? (c.preset_text as string)
              : "",
      };
    case "Textarea": {
      const legacyFields = (c as Record<string, unknown>).textareaFields;
      const fields = Array.isArray(legacyFields)
        ? (legacyFields as unknown[]).map((f) => migrateTextInputField(f))
        : (() => {
            const placeholder = pickOptionalString(c.textareaPlaceholder);
            return [
              placeholder !== undefined
                ? { placeholder }
                : createTaskEditorTextInputField(),
            ];
          })();
      return { type: "text_input", fields };
    }
    case "ButtonGroup":
      return {
        type: "button_branch",
        options: Array.isArray(c.options)
          ? c.options.map((o) => migrateButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "ButtonSelection":
      return {
        type: "button_default",
        allow_multiple_selection: c.allowMultipleSelection !== false,
        options: Array.isArray(c.options)
          ? c.options.map((o) => migrateButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    case "Quiz":
      return {
        type: "button_quiz",
        options: Array.isArray(c.options)
          ? c.options.map((o) => migrateButtonOption(o))
          : [createTaskEditorButtonOption(), createTaskEditorButtonOption()],
      };
    default:
      return null;
  }
}

function isDescriptionComponent(
  c: TaskEditorComponent
): c is TaskEditorDescriptionComponent {
  return isTaskEditorDescriptionComponentType(c.type);
}

function isActionComponent(
  c: TaskEditorComponent
): c is TaskEditorActionComponent {
  return isTaskEditorActionComponentType(c.type);
}

function migrateLegacyBlock(value: unknown): TaskEditorBlock | null {
  if (!value || typeof value !== "object") return null;
  const b = value as Record<string, unknown>;
  const rawType = typeof b.type === "string" ? b.type : "";

  // legacy LastCard / finish / last_card 는 drop (모델에서 제거됨)
  if (
    rawType === "finish" ||
    rawType === "last_card" ||
    rawType === "LastCard"
  ) {
    return null;
  }

  // legacy Header1 → 첫 컴포넌트가 header-style text 인 블록
  if (rawType === "Header1") {
    return {
      type: "header",
      description: [
        {
          type: "text",
          content: typeof b.content === "string" ? (b.content as string) : "",
          typography_style: "header",
        },
      ],
      action: null,
    };
  }

  // 새 shape: description + action 직접
  if (Array.isArray(b.description) || "action" in b) {
    const description = (Array.isArray(b.description) ? b.description : [])
      .map((c) => migrateLegacyComponent(c))
      .filter((c): c is TaskEditorComponent => c !== null)
      .filter(isDescriptionComponent);
    const actionRaw = b.action;
    let action: TaskEditorActionComponent | null = null;
    if (actionRaw && typeof actionRaw === "object") {
      const migrated = migrateLegacyComponent(actionRaw);
      if (migrated && isActionComponent(migrated)) {
        action = migrated;
      }
    }
    const finalDescription =
      description.length > 0
        ? description
        : [createTaskEditorDescriptionComponent("text")];
    const blockTypeFromRaw: TaskEditorBlockType | undefined =
      rawType === "header" || rawType === "body" ? rawType : undefined;
    return {
      type:
        blockTypeFromRaw ??
        deriveTaskEditorBlockType({ description: finalDescription }),
      description: finalDescription,
      action,
    };
  }

  // legacy shape: components 또는 lines (flat 배열) — 섹션별로 분리
  const rawComponents = Array.isArray(b.components)
    ? b.components
    : Array.isArray(b.lines)
      ? b.lines
      : [];
  const migrated = rawComponents
    .map((c) => migrateLegacyComponent(c))
    .filter((c): c is TaskEditorComponent => c !== null);
  const description = migrated.filter(isDescriptionComponent);
  // 액션은 첫 번째만 채택 (max-1 invariant)
  const action = migrated.find(isActionComponent) ?? null;
  const finalDescription =
    description.length > 0
      ? description
      : [createTaskEditorDescriptionComponent("text")];

  return {
    type: deriveTaskEditorBlockType({ description: finalDescription }),
    description: finalDescription,
    action,
  };
}

export function migrateTaskEditorStateFromLegacy(
  value: unknown
): TaskEditorState {
  if (!value || typeof value !== "object") {
    return {
      task_id: "",
      title: "",
      set_number: UNCATEGORIZED_SET_NUMBER,
      // 새/빈 활동지의 첫 블록은 헤더 기본값.
      blocks: [createTaskEditorBlock("header")],
    };
  }
  const s = value as Record<string, unknown>;
  const task_id =
    typeof s.task_id === "string"
      ? s.task_id
      : typeof s.taskId === "string"
        ? (s.taskId as string)
        : "";
  const title = typeof s.title === "string" ? s.title : "";
  const set_number_raw = s.set_number ?? s.setNumber;
  // set_number 는 필수값 — 없거나 숫자가 아니면 미분류(9999)로 기본값.
  const set_number =
    typeof set_number_raw === "number" && Number.isFinite(set_number_raw)
      ? (set_number_raw as number)
      : UNCATEGORIZED_SET_NUMBER;
  const is_key_activity =
    (s.is_key_activity ?? s.isKeyActivity) === true ? true : undefined;
  const rawBlocks = Array.isArray(s.blocks) ? s.blocks : [];
  const blocks = rawBlocks
    .map((b) => migrateLegacyBlock(b))
    .filter((b): b is TaskEditorBlock => b !== null);
  return {
    task_id,
    title,
    set_number,
    ...(is_key_activity !== undefined ? { is_key_activity } : {}),
    // 유효 블록이 없으면 헤더 블록 1개로 폴백 (새/빈 활동지 첫 블록 = 헤더).
    blocks: blocks.length > 0 ? blocks : [createTaskEditorBlock("header")],
  };
}
